"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { CheckCircle, AlertCircle, Info, X } from "lucide-react"

interface ToastProps {
  type: "success" | "error" | "info"
  message: string
  onClose: () => void
  duration?: number
  index: number // Para posicionamiento vertical
}

const Toast: React.FC<ToastProps> = ({ type, message, onClose, duration = 4000, index }) => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    setIsVisible(true)
    const timer = setTimeout(() => {
      setIsVisible(false)
      setTimeout(onClose, 300) // Wait for animation to complete
    }, duration)

    return () => clearTimeout(timer)
  }, [duration, onClose])

  const icons = {
    success: CheckCircle,
    error: AlertCircle,
    info: Info,
  }

  // Glassmorphism colors matching the button style
  const glassColors = {
    success: "bg-green-500/20 backdrop-blur-sm border-green-400/30 text-green-800",
    error: "bg-red-500/20 backdrop-blur-sm border-red-400/30 text-red-800",
    info: "bg-blue-500/20 backdrop-blur-sm border-blue-400/30 text-blue-800",
  }

  const Icon = icons[type]

  // Calculate vertical position based on index (from bottom up)
  const bottomPosition = 20 + (index * 80) // 20px initial + 80px spacing between toasts

  return (
    <div
      className="fixed left-4 z-50 max-w-sm w-full transition-all duration-300 transform"
      style={{
        bottom: `${bottomPosition}px`,
        transform: `${isVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)'}`,
        opacity: isVisible ? 1 : 0,
      }}
    >
      <div className={`p-4 rounded-xl border ${glassColors[type]} shadow-lg hover:shadow-xl transition-all duration-300`}>
        <div className="flex items-start">
          <Icon className="h-5 w-5 mt-0.5 mr-3 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium">{message}</p>
          </div>
          <button 
            onClick={() => setIsVisible(false)} 
            className="ml-3 flex-shrink-0 hover:opacity-70 transition-opacity duration-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default Toast
